<script setup lang="ts">
import type { CalendarDraftPeriod, CalendarPeriod } from '@ui/components/calendar/picker'
import { CellState } from '@ui/components/calendar/picker'
import CalendarDay from '@ui/components/calendar/picker/day/CalendarDay.vue'
import { isHoverPointer } from '@ui/utils/hover'
import {
  compareAsc,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { ru } from 'date-fns/locale'

const props = withDefaults(
  defineProps<{
    modelValue?: Date | CalendarDraftPeriod
    /** Месяц для просмотра */
    month?: Date
    today?: Date
    range?: boolean
    disabledDate?: (date: Date) => boolean
  }>(),
  {
    month: () => new Date(),
    today: () => new Date(),
    range: false,
  },
)

const emit = defineEmits<{ 'update:modelValue': [day: Date] }>()
const possibleEndDate = ref<Date>()
const period = computed(() => (props.modelValue instanceof Date ? undefined : props.modelValue))
function onHover(event: PointerEvent, day: Date) {
  if (!isHoverPointer(event)) {
    return
  }

  /** Без отрисовки периода при выборе начальной даты */
  if (!props.range || !period.value?.start || period.value.end || props.disabledDate?.(day)) {
    return
  }
  possibleEndDate.value = day
}

function selectDay(day: Date) {
  if (!props.disabledDate?.(day)) {
    possibleEndDate.value = day
    emit('update:modelValue', day)
  }
}

function getState(date: Date) {
  if (props.disabledDate?.(date)) {
    return CellState.Disabled
  }

  if (props.range) {
    const first = period.value?.start
    const last = period.value?.end || possibleEndDate.value
    if (!first) {
      return CellState.Default
    }
    if (!last) {
      return isSameDay(first, date) ? CellState.Start : CellState.Default
    }
    const { start, end }: CalendarPeriod =
      compareAsc(first, last) <= 0 ? { start: first, end: last } : { start: last, end: first }
    if (isSameDay(start, date)) {
      return isSameDay(end, date) ? CellState.Selected : CellState.Start
    }
    if (isSameDay(end, date)) {
      return CellState.End
    }
    if (start < date && date < end) {
      return CellState.Mid
    }
  } else if (props.modelValue instanceof Date && isSameDay(props.modelValue, date)) {
    return CellState.Selected
  }

  return CellState.Default
}

/** ru для недели с пн */
const days = computed(() =>
  eachDayOfInterval({
    start: startOfWeek(startOfMonth(props.month), { locale: ru }),
    end: endOfWeek(endOfMonth(props.month), { locale: ru }),
  }),
)

const daysWithState = computed(() =>
  days.value.map(day => ({
    day,
    key: day.getTime(),
    label: day.getDate(),
    state: getState(day),
    today: isSameDay(props.today, day),
    outside: !isSameMonth(props.month, day),
  })),
)
</script>

<template lang="pug">
.display-grid.grid-cols-7.row-gap-12.column-gap-8.overflow-hidden
  CalendarDay(
    v-for="day of daysWithState"
    :key="day.key"
    data-id="calendar-day"
    :data-date="format(day.day, 'yyyy-MM-dd')"
    :label="day.label"
    class="w-100p"
    :state="day.state"
    :today="day.today"
    :outside="day.outside"
    @click="selectDay(day.day)"
    @pointerenter="onHover($event, day.day)"
  )
</template>
